import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { DataManager } from '../../core/data-manager';
import { ResourceManager } from '../../core/resource-manager';
import { ApiManager } from '../../core/api-manager';


/**
 * Carica un template .docx dall'entity "intestazioni" tramite keyField.
 * Ritorna un ArrayBuffer del file .docx, oppure null se non trovato.
 */
export async function loadTemplate(keyField) {
    let constraints = [
        { fieldName: "keyField", type: "value", value: keyField }
    ];
    var sorting = { field: 'id', order: 'desc' };
    var res = await DataManager.searchItems('intestazioni', 1, 10, sorting, '', [], constraints);

    if (res.success !== 1 || !res.body || res.body.length === 0) {
        return null;
    }

    const item = res.body[0];
    if (!item.file || !item.file.hash) {
        return null;
    }

    const fileRes = await ResourceManager.getFile(item.file.hash, 'intestazioni');
    if (fileRes.success !== 1 || !fileRes.base64) {
        return null;
    }

    // Converti base64 in ArrayBuffer
    const binaryString = atob(fileRes.base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}


/**
 * Riempe un template .docx con i dati forniti usando docxtemplater.
 * @param {ArrayBuffer} templateBuffer - il file .docx come ArrayBuffer
 * @param {Object} data - oggetto con i tag da sostituire (es. { internalCode: '123', rows: [...] })
 * @returns {Blob} - il .docx generato come Blob
 */
export function fillTemplate(templateBuffer, data) {
    const zip = new PizZip(templateBuffer);
    const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        delimiters: { start: '{', end: '}' }
    });

    try {
        doc.render(data);
    } catch (error) {
        if (error.properties && error.properties.errors) {
            console.error('Dettaglio errori template DOCX:');
            error.properties.errors.forEach((e, i) => {
                console.error(`  Errore ${i + 1}:`, e.message, '| Tag:', e.properties?.id || e.properties?.xtag || 'N/A');
            });
        }
        throw error;
    }

    const out = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });

    return out;
}


/**
 * Converte un Blob in base64 string.
 */
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            const base64Index = dataUrl.indexOf(';base64,');
            if (base64Index === -1) {
                reject(new Error('Errore conversione base64'));
                return;
            }
            resolve(dataUrl.substring(base64Index + ';base64,'.length));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}


/**
 * Invia un .docx (come Blob) al backend per la conversione in PDF.
 * Ritorna l'oggetto risposta completo { pdf_base64, hash, entity, filename }, oppure null in caso di errore.
 */
export async function convertToPdf(docxBlob, filename, entity, idRecord) {
    const docxBase64 = await blobToBase64(docxBlob);

    const params = {
        docx_base64: docxBase64,
        filename: filename || 'document'
    };
    if (entity) {
        params.entity = entity;
    }
    if (idRecord) {
        params.idRecord = idRecord;
    }

    const res = await ApiManager.sendAuthenticatedRequest('/custom/convert_docx_to_pdf', params);

    if (res.success === 1 && res.pdf_base64) {
        return res;
    }

    return null;
}


/**
 * Helper per scaricare un Blob come file.
 */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}


/**
 * Helper per scaricare un base64 come file PDF.
 */
export function downloadPdfBase64(base64, filename) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    downloadBlob(blob, filename);
}
